import { useMutation } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useContextSelector } from "use-context-selector";
import qs from "qs";
import {
  AddSuppliersByTgramsIdToOpportunityMutationResult,
  AddSuppliersByTgramsIdToOpportunityQuery,
  ClaimGuestOpportunityMutationResult,
  ClaimGuestOpportunityMutationVariables,
  ClaimGuestOpportunityQuery,
} from "../../../schema/generated/schema";
import { useQueryParams } from "../../../utils/routerUtils";
import LoadingSpinner from "../../LoadingSpinner";
import { useConfig } from "../../proposals/contexts/configContext";
import UserContext from "../../proposals/contexts/userContext";
import ClaimErrorBoundary from "./ClaimErrorBoundary";
import { sanitizeHeading } from "../utils";

const OpportunityClaimRoute = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { buyerEmail, buyerFullReg, claimCode, destination, heading } =
    useQueryParams();
  const { origins } = useConfig();

  const loggedInUser = useContextSelector(UserContext, ctx => ({
    data: ctx.loggedInUser,
    loading: ctx.loggedInUserLoading,
    error: ctx.loggedInUserLoading,
  }));

  const [claimGuestOpportunity, { called: claimCalled }] = useMutation<
    ClaimGuestOpportunityMutationResult,
    ClaimGuestOpportunityMutationVariables
  >(ClaimGuestOpportunityQuery);

  const [addSuppliersByTgramsIdToOpportunity] =
    useMutation<AddSuppliersByTgramsIdToOpportunityMutationResult>(
      AddSuppliersByTgramsIdToOpportunityQuery
    );

  const getClaimURL = useCallback(
    (queryParams: any) => {
      const baseURL = `${window.location.origin}/project/claim/${projectId}`;
      return queryParams ? `${baseURL}?${qs.stringify(queryParams)}` : baseURL;
    },
    [projectId]
  );

  useEffect(() => {
    if (loggedInUser.loading || claimCalled) {
      return;
    }

    if (loggedInUser.error) {
      throw new Error("There was an issue retrieving logged in user info");
    }

    if (!projectId) {
      throw new Error("Missing or invalid project id");
    }

    if (claimCode && typeof claimCode !== "string") {
      throw new Error("Invalid email verification code");
    }

    // if buyer is not fully registered, redirect to registration. pre-fill email if provided.
    if (buyerFullReg === "false") {
      const regDestination = getClaimURL({
        buyerFullReg: true,
        buyerEmail,
        claimCode,
        destination,
      });
      const prefillEmail = buyerEmail ?? "";
      window.location.href = `${
        origins.thomasnetHome
      }/account/register?${qs.stringify({
        url: regDestination,
        email: prefillEmail,
      })}`;
      return;
    }

    // if buyer is fully registered & not logged in, redirect to login. pre-fill email if provided.
    if (!loggedInUser.data) {
      const loginDestination = getClaimURL({
        buyerFullReg: true,
        claimCode,
        destination,
      });
      const prefillEmail = buyerEmail ?? "";
      window.location.href = `${
        origins.thomasnetHome
      }/account/login?${qs.stringify({
        forceVerification: "true",
        url: loginDestination,
        email: prefillEmail,
      })}`;
      return;
    }

    // once logged in, claim opportunity using opportunity id and claim code
    claimGuestOpportunity({
      variables: {
        opportunityId: projectId,
        emailVerifyCode: claimCode ?? null,
      },
    })
      .then(async res => {
        // once claimed, send user to project table
        const tgramsIds = res.data?.claimGuestOpportunity?.tgramsIds;

        if (!tgramsIds) {
          throw new Error("No suppliers to send project to");
        }

        return addSuppliersByTgramsIdToOpportunity({
          variables: {
            opportunityId: projectId,
            tgramsIds,
            headingId: sanitizeHeading(heading ?? null),
          },
        }).then(() => {
          window.location.href =
            typeof destination === "string"
              ? destination
              : `${origins.thomasnetHome}/account/requests`;
        });
      })
      .catch(() => {
        throw new Error("There was an issue claiming the project");
      });
  }, [
    buyerEmail,
    buyerFullReg,
    claimCalled,
    claimCode,
    destination,
    loggedInUser,
    origins,
    projectId,
    addSuppliersByTgramsIdToOpportunity,
    claimGuestOpportunity,
    getClaimURL,
  ]);

  return <LoadingSpinner />;
};

const WithErrorBoundary = () => (
  <ClaimErrorBoundary>
    <OpportunityClaimRoute />
  </ClaimErrorBoundary>
);

export default WithErrorBoundary;
